import { LANG_LOCALES, type Lang } from './types'

/**
 * Formats dépendants de la langue.
 *
 * Tout passe par `Intl` : un nombre figé en `fr-FR` affiche « 1 234 » à un
 * lecteur anglophone qui attend « 1,234 », et une date en jj/mm/aaaa se lit à
 * l'envers d'un lecteur qui attend mm/jj. Les séparateurs ne sont jamais
 * écrits à la main.
 */
export interface Formatters {
  /** 1 234 / 1,234 */
  number: (n: number) => string
  /** 12,5 / 12.5 — `digits` décimales, séparateur local. */
  decimal: (n: number, digits?: number) => string
  /** 12,5 % / 12.5% */
  percent: (part: number, total: number, digits?: number) => string
  /** 02/11/2026 */
  date: (iso: string | Date | null | undefined) => string
  /** 02/11/2026 14:30 */
  dateTime: (iso: string | Date | null | undefined) => string
  /** lun. 2 — étiquettes d'axe compactes. */
  dayShort: (iso: string | Date) => string
  /** « aujourd'hui », « depuis 3 jours » */
  sinceDays: (iso: string) => string
  /** Code de langue BCP 47, pour les appels `Intl` ponctuels. */
  locale: string
}

function parse(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

const SINCE: Record<Lang, { today: string; yesterday: string; days: (n: number) => string }> = {
  fr: {
    today: "aujourd'hui",
    yesterday: 'depuis hier',
    days: (n) => `depuis ${n} jours`,
  },
  en: {
    today: 'today',
    yesterday: 'since yesterday',
    days: (n) => `for ${n} days`,
  },
}

export function formattersFor(lang: Lang): Formatters {
  const locale = LANG_LOCALES[lang]
  return {
    locale,
    number: (n) => n.toLocaleString(locale),
    decimal: (n, digits = 1) =>
      n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }),
    percent: (part, total, digits = 1) => {
      const ratio = total ? part / total : 0
      return ratio.toLocaleString(locale, {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    },
    date: (value) => {
      const d = parse(value)
      return d ? d.toLocaleDateString(locale) : '—'
    },
    dateTime: (value) => {
      const d = parse(value)
      return d
        ? d.toLocaleDateString(locale) +
            ' ' +
            d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
        : '—'
    },
    dayShort: (value) => {
      const d = parse(value)
      return d ? d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' }) : '—'
    },
    sinceDays: (iso) => {
      const d = parse(iso)
      if (!d) return '—'
      const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
      const words = SINCE[lang]
      if (days <= 0) return words.today
      if (days === 1) return words.yesterday
      return words.days(days)
    },
  }
}
